import React, { useEffect, useState } from 'react'
import { FiCheck, FiSlash } from 'react-icons/fi'
import Button from './UI/Button'
import T from './UI/T'
import Label from './UI/Label'
import Checkbox from './UI/Checkbox'
import Title from './UI/Title'

function SurveyEmail({ question, tag, answer, setAnswer, required }) {
  useEffect(() => { setAnswer('', false) }, [])
  return (
    <Label title={question}>
      <input
        name={question.tag}
        className="form-input font-medium border-gray-700 border-2 rounded text-lg lg:text-xl py-1 lg:py-3 w-full text-gray-800 bg-white placeholder-gray-800 placeholder-opacity-25"
        placeholder=""
        type="email"
        value={answer}
        onChange={e => setAnswer(e.target.value, !e.target.value.match(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/))} />
    </Label>
  )
}

function SurveyInteger({ question, tag, setAnswer, answer, required }) {
  useEffect(() => { setAnswer('', false) }, [])
  return (
    <Label title={question}>
      <input
        name={question.tag}
        className="form-input font-medium border-gray-700 border-2 rounded text-lg lg:text-xl py-1 lg:py-3 w-full text-gray-800 bg-white placeholder-gray-800 placeholder-opacity-25"
        placeholder=""
        type="text"
        pattern="\d*"
        value={answer}
        onChange={e => setAnswer(e.target.value, false)} />
    </Label>
  )
}

function SurveyText({ question, tag, setAnswer, answer, required }) {
  useEffect(() => { setAnswer('', false) }, [])
  return (
    <Label title={question}>
      <input
        name={question.tag} 
        className="form-input font-medium border-gray-700 border-2 rounded text-lg lg:text-xl py-1 lg:py-3 w-full text-gray-800 bg-white placeholder-gray-800 placeholder-opacity-25"
        placeholder=""
        type="text"
        value={answer}
        onChange={e => setAnswer(e.target.value, e.target.value < 3)} />
    </Label>
  )
}


function SurveyChoice({ question, tag, description, setAnswer, answer }) {
  return (
    <Label title={question}>
      <div className="flex flex-col lg:flex-row justify-center flex-wrap items-center">
        <select
          className="form-input font-medium text-lg lg:text-xl py-1 lg:py-3 w-full overflow-hidden flex justify-center items-center align-middle focus:outline-none select-none flex-shrink-0 text-gray-800 bg-white border-gray-700 border-2 rounded uppercase"
          value={answer}
          onChange={e => setAnswer(e.target.answer)}
          style={{ backgroundColor: 'transparent' }}>
          <option answer=""> - </option>
          {description.split(';').map(d => 
            <option answer={d}>{d}</option>
          )}
        </select>
      </div>
    </Label>
  )
}

function SurveyMultiCheck({ question, tag, description, setAnswer, answer }) {
  useEffect(() => { setAnswer(description, false) }, [])
  const options = description.split(';')
  const selected = (answer || '').split(';')
  return (
    <div className="flex flex-col items-start">
      {options.map(o =>
        <div className="flex flex-row justify-center my-1 lg:my-1 px-1 lg:px-2 items-start cursor-pointer" onClick={_ => setAnswer(selected.includes(o) ? selected.filter(s => s !== o).join(';') : {...selected, o})}>
          <Checkbox variant="small" checked={selected.includes(o)} onClick={_ => setAnswer(selected.includes(o) ? selected.filter(s => s !== o).join(';') : {...selected, o})} className="mr-3" />
          <div className="text-sm lg:text-base leading-tight lg:leading-snug select-none mt-1">
            <div dangerouslySetInnerHTML={{ __html: o }} />
          </div>
        </div>
      )}
    </div>
  )
  }


function SurveyCheck({ question, tag, description, setAnswer, answer }) {
  useEffect(() => { setAnswer('false', false) }, [])
  return (
    <div className="flex flex-row justify-center my-3 lg:my-6 px-1 lg:px-3 items-start cursor-pointer" onClick={_ => setAnswer(answer === 'true' ? 'false' : 'true')}>
      <Checkbox checked={answer === 'true'} onClick={_ => setAnswer(answer === 'true' ? 'false' : 'true')} className="mr-4" />
      <div className="text-base lg:text-lg leading-tight lg:leading-snug select-none -mt-2">
        <div dangerouslySetInnerHTML={{ __html: question }} />
      </div>
    </div>
  )
}

function SurveyAccept({ question, description, answer }) {
  return (
    <>
      <Label><span dangerouslySetInnerHTML={{ __html: question }} /></Label>
      <p>{description}</p>
      <div className="flex flex-col lg:flex-row justify-center flex-wrap items-center">
        <Button Icon={FiSlash} className="m-3 lg:mx-3" onClick={() => answer('false')}><T>editor.no</T></Button>
        <Button Icon={FiCheck} className="m-3 lg:mx-3" onClick={() => answer('true')}><T>editor.yes</T></Button>
      </div>
    </>
  )
}

export default function Survey({ questions, confirmAnswers }) {
  const [answers, setAnswers] = useState([])
  const answer = (survey_question_id, answer, error) => {
    setAnswers(as => [...as.filter(a => a.survey_question_id !== survey_question_id), { survey_question_id, answer, error }])
  }
  return (
    <div className="flex flex-col justify-center items-center w-full min-h-screen lg:max-w-5xl mx-auto p-4 lg:p-6">
      <style dangerouslySetInnerHTML={{ __html: `
        body { overflow-y: auto !important; }        
      ` }} />
      <div className="text-3xl lg:text-4xl font-bold mb-10 lg:mb-16 max-w-lg text-center uppercase leading-tight"><T>survey.title</T></div>
      <div className="flex flex-col justify-center items-center max-w-lg">
        {questions.filter(q => q.active).sort((a, b) => a.order - b.order).map(({ question, question_type: type, description, required, id, tag }) => (
          <>
            {type === 'mail' && <SurveyEmail key={id} setAnswer={(v, e) => answer(id, v, e)} answer={answers.find(a => a.survey_question_id === id)?.answer} question={question} tag={tag} description={description} required={required} />}
            {type === 'choice' && <SurveyChoice key={id} setAnswer={(v, e) => answer(id, v, e)} answer={answers.find(a => a.survey_question_id === id)?.answer} question={question} tag={tag} description={description} />}
            {type === 'check' && <SurveyCheck key={id} setAnswer={(v, e) => answer(id, v, e)} answer={answers.find(a => a.survey_question_id === id)?.answer} question={question} tag={tag} description={description} />}
            {type === 'multi_choice' && <SurveyMultiCheck key={id} setAnswer={(v, e) => answer(id, v, e)} answer={answers.find(a => a.survey_question_id === id)?.answer} question={question} tag={tag} description={description} />}
            {/* {type === 'accept' && <SurveyAccept key={id} setAnswer={(v, e) => answer(id, v, e)} answer={answers.find(a => a.survey_question_id === id)?.answer} question={question} tag={tag} description={description} />} */}
            {type === 'integer' && <SurveyInteger key={id} setAnswer={(v, e) => answer(id, v, e)} answer={answers.find(a => a.survey_question_id === id)?.answer} question={question} tag={tag} description={description} />}
            {type === 'simple_text' && <SurveyText key={id} setAnswer={(v, e) => answer(id, v, e)} answer={answers.find(a => a.survey_question_id === id)?.answer} question={question} tag={tag} description={description} required={required} />}
          </>
        ))}
      </div>
      <div className="flex justify-center mt-3 lg:mt-12 mb-4 flex-wrap items-center">
        {false &&
          <Button Icon={FiSlash} className="mx-1 lg:mx-3" onClick={() => answer('')}><T>editor.escape</T></Button>
        }
        <Button
          Icon={FiCheck}
          className="mx-1 lg:mx-3"
          onClick={() => confirmAnswers(answers)}
          disabled={answers.find(a => a.error)}>
          <T>editor.send</T>
        </Button>
      </div>
    </div>
  )
}
